import {BehaviorSubject} from "rxjs/BehaviorSubject";
import {Observable, ObservableInput} from "rxjs/Observable";

import "rxjs/add/observable/empty";
import "rxjs/add/operator/every";
import "rxjs/add/operator/mergeMap";
import "rxjs/add/observable/of";
import "rxjs/add/operator/finally";
import "rxjs/add/operator/do";

import {ChartIQDataPoint, ChartIQFeed, ChartIQFeedCallback, ChartIQFeedParams} from "../types/ChartIQFeed";
import {ChartIQInterval} from "../types/ChartIQ";
import {Scheduler} from "rxjs";
import {Pair, PairOf, Predicate} from "../../../types/GenericTypes";
import {DateTimeUtil} from "../../../js/util/DateTimeUtil";

export abstract class ChartIQDataFeed implements ChartIQFeed {

    private loading$ = new BehaviorSubject<boolean>(false);

    public get loading(): Observable<boolean> {
        return this.loading$.observeOn(Scheduler.async);
    }

    protected abstract retrieveData(symbol: string,
                                    startDate: Date,
                                    endDate: Date | null,
                                    period: number | null,
                                    interval: ChartIQInterval | null): Promise<Array<ChartIQDataPoint>>;

    public fetchInitialData(symbol: string,
                            startDate: Date,
                            endDate: Date,
                            params: ChartIQFeedParams,
                            cb: ChartIQFeedCallback) {
        this.fetchInitialDataWithLookback(symbol, startDate, endDate, params, cb);
    }

    private async fetchInitialDataWithLookback(symbol: string,
                                               startDate: Date,
                                               endDate: Date,
                                               params: ChartIQFeedParams,
                                               cb: ChartIQFeedCallback) {
        let count = 0;
        let data: Array<ChartIQDataPoint> = [];
        let start = startDate;
        let end = endDate;

        const insideWindow = this.isInsideWindowLimit(startDate);
        try {
            this.loading$.next(true);
            do {
                const window = this.moveDataWindow(start, end, count++);
                start = window[0];
                end = window[1];

                try {
                    data = await this.retrieveData(symbol, startDate, endDate, params.period, params.interval);
                } catch (e) {
                    data = [];
                }
            } while (data.length === 0 && insideWindow(start));

            cb({quotes: data});
        } catch (e) {
            cb({quotes: []});
        } finally {
            this.loading$.next(false);
        }
    }

    private moveDataWindow(startDate: Date, endDate: Date, step: number): Pair<Date> {
        const startTime = startDate.getTime();
        const endTime = endDate.getTime();
        const diff = endTime - startTime;
        const timeMove = diff * step;

        const newStartTime = startTime - timeMove;
        const newEndTime = endTime - timeMove;
        return PairOf(new Date(newStartTime), new Date(newEndTime));
    }

    private isInsideWindowLimit(initialDate: Date): Predicate<Date> {
        return (dateToCheck) => DateTimeUtil.daysBetween(initialDate, dateToCheck) < 7;
    }

    public fetchUpdateData(symbol: string,
                           startDate: Date,
                           params: ChartIQFeedParams,
                           cb: ChartIQFeedCallback) {
        const market = params.chart.market;
        const isOpen = market.isOpen();
        if (isOpen != null && isOpen) {
            const response = this.retrieveData(symbol, startDate, null, params.period, params.interval);
            this.fetchData(response, cb);
        } else {
            cb({quotes: []});
        }
    }

    public fetchPaginationData(symbol: string,
                               startDate: Date,
                               endDate: Date,
                               params: ChartIQFeedParams,
                               cb: ChartIQFeedCallback) {
        const response = this.retrieveData(symbol, startDate, endDate, params.period, params.interval);
        this.fetchData(response, cb);
    }

    private fetchData(obs: ObservableInput<Array<ChartIQDataPoint>>, cb: ChartIQFeedCallback) {
        Observable.of(null) // Dummy Data to start the chain
                  .do(() => this.loading$.next(true))
                  .mergeMap(() => obs)
                  .finally(() => this.loading$.next(false))
                  .subscribe(series => cb({quotes: series}));
    }
}
